/* eslint-disable max-len */
/* eslint-disable no-unused-vars */

import { html, PolymerElement } from "@polymer/polymer/polymer-element.js";
import template from "./ns-sortable.html";
import { NsSlottedDropdown } from "@nuskin/ns-form-elements";

/**
 * Used for reordering a list of items.
 *
 * <h1>Item List Sorting</h1>
 * <a href="//developers.dev.nuskin.com/playground/ns-sortable.html">Click here for DEMO</a>
 * <marked-element>
 *  <div slot="markdown-html">
 *  <script type="text/markdown">
 *      ### Styling
 *
 *      `<ns-sortable>` provides the following custom properties and mixins for styling:
 *       <br/>
 *       <br/>
 *
 *      Custom property               | Description                                    | Default
 *      :-----------------------------|:-----------------------------------------------|:------------------
 *      `--ns-sortable-item`          | Mixin for additional styling for sortable item | `{}`
 *      <img width="250"/>            | <img width="250"/>                             |
 *
 *  </script>
 * </marked-element>
 */
export class NsSortable extends PolymerElement {
  constructor() {
    super();
    this.customActionFuncPrefix = "customAction";
    this.animationDuration = 250;
    this.itemMargin = 10;
  }

  static get template() {
    let htmlElement = document.createElement("TEMPLATE");
    htmlElement.innerHTML = template;
    return htmlElement;
  }

  static get is() {
    return "ns-sortable";
  }

  static get properties() {
    return {
      /**
       * List for reordering
       */
      items: {
        type: Array,
        notify: true,
        observer: "_itemsListChange"
      },
      /**
       * Indicates whether the content of each item is inetractive (has form elements) or not
       * which affects styles of draggable items
       */
      interactive: {
        type: Boolean
      },
      /**
       * Actions translations
       */
      editActionText: { type: String, value: "Edit" },
      removeActionText: { type: String, value: "Remove item" },
      moveUpActionText: { type: String, value: "Move up" },
      moveDownActionText: { type: String, value: "Move down" },
      moveToTopActionText: { type: String, value: "Move to top" },
      moveToBottomActionText: { type: String, value: "Move to bottom" },
      /**
       * Custom actions for edit drop down
       */
      customActions: {
        type: Object,
        observer: "_onCustomActions"
      },
      selectedText: {
        type: String,
        observer: "_handleSelectedText"
      },
      /**
       * Identifies the property inside an individual item that should be used as a title
       */
      propertyForTitle: {
        type: String
      },
      /**
       * Identifies whether the list can be empty
       */
      required: {
        type: Boolean
      },
      valid: {
        type: Boolean
      },
      minItems: {
        type: Number
      }
    };
  }

  connectedCallback() {
    super.connectedCallback();

    this.set("selectedTextBackup", this.editActionText);
    this.set("selectedText", this.editActionText);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.$.items.querySelectorAll(NsSlottedDropdown.is).forEach((node) => {
      node.removeEventListener(
        "selected-text-changed",
        this._handleNsDropDown.bind(this)
      );
    });

    this.removeEventListeners();
    cancelAnimationFrame(this.animFrame);
  }

  _onCustomActions(newVal) {
    if (newVal) {
      newVal.forEach((item, index) => {
        this.set(`${this.customActionFuncPrefix}${index}`, item.func);
      });
    }
  }

  _onCustomActionClick(e) {
    let i = e.currentTarget.dataset.index;
    this[`${this.customActionFuncPrefix}${i}`](e);
  }

  _itemsListChange() {
    if (this.DnDdata) {
      this.DnDdata.items.forEach((item, index) => {
        item.style = this.getItemStyles(index);
      });
      this.classesCleanup();
    }

    this.resizeItemsContainer();

    this.disconnectedCallback();
    this.$.items.querySelectorAll(NsSlottedDropdown.is).forEach((node) => {
      // node.removeEventListener('selected-text-changed', this._handleNsDropDown.bind(this));
      node.addEventListener(
        "selected-text-changed",
        this._handleNsDropDown.bind(this)
      );
    });

    if (this.interactive) {
      this.$.items.querySelectorAll(".ns-sortable-item").forEach((item) => {
        item.className +=
          item.className.indexOf("interactive") === -1 ? " interactive" : "";
      });
    }
  }

  _handleSelectedText() {
    this.set("selectedText", this.selectedTextBackup);
  }

  _handleNsDropDown(e) {
    e.currentTarget.selectedItem = {};
    e.currentTarget.$.dropdownListbox.selected = null;
  }

  getItemStyles(index, skipZindex) {
    if (!this.DnDdata.items.length) return;
    let pixVal = 0;
    for (let i = 0; i < index; i++) {
      let elem = this.DnDdata.items.find((item) => item.dragOrder === i);
      if (!elem) {
        // prevents breaking in situation
        // where drag is too compulsive and too fast
        // and `dragOrder` gets incorrect values
        return;
      }
      pixVal += elem.clientHeight + 2;
    }
    return `
      transform: translateY(${pixVal + index * this.itemMargin}px); 
      -moz-transform: translateY(${pixVal + index * this.itemMargin}px); 
      -webkit-transform: translateY(${pixVal + index * this.itemMargin}px); 
      ${skipZindex ? "" : "z-index: " + (this.items.length - index)}
    `;
  }

  onTheFlyStyleUpdate(val) {
    return `
      transform: translateY(${val}px); 
      -moz-transform: translateY(${val}px); 
      -webkit-transform: translateY(${val}px); 
      `;
  }

  // creates a 100ms second interval that runs for 2 seconds and that checks if DOM has rendered items. If so, interval is cleared out and styles get applyed
  resizeItemsContainer() {
    this.$.items.className = "mute";
    let timeout = 20; // 20 of 100ms = 2 seconds
    let step = 100;
    let index = 0;

    let isDOMReady = setInterval(() => {
      index++;
      if (index === timeout) clearInterval(isDOMReady);

      this.set("DnDdata", this.DnDdata || {});
      this.set(
        "DnDdata.items",
        Array.prototype.slice.call(
          this.$.items.querySelectorAll(".ns-sortable-item")
        )
      );

      if (this.DnDdata.items && this.DnDdata.items.length) {
        clearInterval(isDOMReady);
        this.DnDdata.items.forEach((item, index) => {
          item.dragOrder = index;
        });
        let height = 0;
        this.DnDdata.items.forEach((item, index) => {
          height += item.clientHeight + this.itemMargin + 2;
          item.style = this.getItemStyles(index);
        });
        this.$.items.style.height = height + "px";
        this.$.items.className = "";
      } else this.$.items.style.height = 0 + "px";
    }, step);
  }

  findParentByClass(elem, className) {
    let found = false;
    while (!found) {
      if (
        (elem.parentElement &&
          elem.parentElement.className.indexOf(className) !== -1) ||
        !elem.parentElement
      ) {
        found = true;
        return elem.parentElement || {};
      }
      elem = elem.parentElement;

      if (!elem) {
        break;
      }
    }
  }

  animateDown(e) {
    this.animate(e, "down");
  }
  animateUp(e) {
    this.animate(e, "up");
  }
  animateTop(e) {
    this.animate(e, "top");
  }
  animateBottom(e) {
    this.animate(e, "bottom");
  }
  animateRemove(e) {
    this.animate(e, "remove");
  }

  animate(e, action) {
    let elem = this.findParentByClass(e.target, "ns-sortable-item");
    let index = +elem.dragOrder;

    // ----------------- Validate
    switch (action) {
      case "up":
        if (index === 0) return;
        break;
      case "down":
        if (index === this.items.length - 1) return;
        break;
    }

    // ---------------- Run animation
    let items = this.DnDdata.items;
    // disable pointer events while animation is running
    items.forEach((item) => {
      item.className += " mute";
    });
    // add styles to animated item
    items[+elem.dataset.index].className += " animated";

    this.set(
      "DnDdata.grabbedElement",
      items.find((item) => item.dragOrder === index)
    );
    this.set("DnDdata.zone", index);
    switch (action) {
      case "up": {
        this.doAnimation(index - 1, true);
        break;
      }
      case "down": {
        this.doAnimation(index + 1, true);
        break;
      }
      case "top": {
        if (!index) break;
        let topInterval = setInterval(() => {
          index--;
          this.doAnimation(index, true);
          if (index === 0) clearInterval(topInterval);
        }, this.animationDuration / items.length);
        break;
      }
      case "bottom": {
        if (index === items.length - 1) break;
        let bottomInterval = setInterval(() => {
          index++;
          this.doAnimation(index, true);
          if (index === items.length - 1) clearInterval(bottomInterval);
        }, this.animationDuration / items.length);
        break;
      }
      case "remove": {
        items.find((item) => item.dragOrder === index).className += " removed";
        items.forEach((item, i) => {
          if (item.dragOrder >= index) {
            this.doAnimation(i, true);
          }
        });
        break;
      }
    }

    // remove animation classes after transition time is over
    // emit reordered list
    setTimeout(() => {
      this.classesCleanup();

      if (action !== "remove") {
        this.dispatchEvent(
          new CustomEvent("on-ns-sorted-list-reorder", {
            detail: {
              reorderedList: this.getReorderedList()
            }
          })
        );
      } else {
        this.dispatchEvent(
          new CustomEvent("on-ns-sorted-item-removed", {
            detail: {
              index: items[index].dataset.index
            }
          })
        );
      }
    }, this.animationDuration * 2);
  }

  // -------------------------------- Drag & Drop --------------------

  // listen to document mouse move event to capture mouse cursor position
  _handleDocumentMouseMove(e) {
    let data;
    if (e.changedTouches) data = e.changedTouches[0];
    else data = e;
    if (this.DnDdata.windowMouseY < data.clientY)
      this.DnDdata.direction = "down";
    if (this.DnDdata.windowMouseY > data.clientY) this.DnDdata.direction = "up";

    this.DnDdata.windowMouseY = data.clientY;
    if (!this.DnDdata.processing && this.DnDdata.mouseDown) {
      this.animFrame = requestAnimationFrame(this.dragAnimation.bind(this));
      this.DnDdata.processing = true;
    }
  }

  hoveredElement(zone) {
    zone = zone < 0 ? 0 : zone;
    zone =
      zone >= this.DnDdata.items.length ? this.DnDdata.items.length - 1 : zone;
    let item = this.DnDdata.items.find((item) => item.dragOrder === zone);
    return item || {};
  }

  classesCleanup() {
    this.DnDdata.items.forEach((item) => {
      item.className = item.className.split(/\s?mute/).join("");
      item.className = item.className.split(/\s?animated/).join("");
      item.className = item.className.split(/\s?no-touch-action/).join("");
      item.className = item.className.split(/\s?removed/).join("");
    });
  }

  _handleDocumentMouseUp() {
    // handle drop only if mouse was pushed down and moved up or down
    if (this.DnDdata.mouseDown) {
      this.DnDdata.grabbedElement.className = this.DnDdata.grabbedElement.className
        .split(/\s?no-transition/)
        .join("");
      // while animation on element drop disable its pointer events
      // to prevent interaction before animation is done
      this.DnDdata.grabbedElement.className += " mute";
      clearInterval(this.mobileAutoScroll);
      this.mobileAutoScroll = null;
      this.DnDdata.grabbedElement.onmouseup = null;
      this.DnDdata.grabbedElement.onmousemove = null;
      this.DnDdata.mouseDown = false;

      this.DnDdata.items[this.DnDdata.index].style = this.getItemStyles(
        this.DnDdata.zone
      );

      // remove animation classes after transition time is over
      // emit reordered list
      setTimeout(() => {
        this.classesCleanup();

        this.dispatchEvent(
          new CustomEvent("on-ns-sorted-list-reorder", {
            detail: {
              reorderedList: this.getReorderedList()
            }
          })
        );

        this.removeEventListeners();

        this.DnDdata.grabbedElement.className = this.DnDdata.grabbedElement.className
          .split(/\s?dragged/)
          .join("");
      }, this.animationDuration);
    }
  }

  getReorderedList() {
    let reorderedList = [];
    this.DnDdata.items.forEach((item) => {
      let data;
      try {
        data = JSON.parse(item.dataset.value); // case where items are objects
      } catch (err) {
        data = item.dataset.value; // case where items are text
      }
      reorderedList[item.dragOrder] = data;
    });
    return reorderedList;
  }

  getZone() {
    let cursorPosition =
      this.DnDdata.windowMouseY - this.$.items.getBoundingClientRect().y;
    let zone = 0;
    let itemsHeightSum = 0;
    while (cursorPosition - itemsHeightSum > 0) {
      if (!this.DnDdata.items[zone]) break;
      itemsHeightSum += this.DnDdata.items[zone].clientHeight + this.itemMargin;
      zone++;
    }
    return zone - 1 >= 0 ? zone - 1 : 0;
  }

  dragAnimation() {
    if (this.DnDdata.mouseDown) {
      let zone = this.getZone();

      // start autoscroll when dragged to the bottom of the mobile screen
      // TODO: do same for the top of the screen
      if (
        window.innerHeight - this.DnDdata.windowMouseY <= 20 &&
        !this.mobileAutoScroll
      ) {
        this.mobileAutoScroll = setInterval(() => {
          window.scrollTo(0, window.scrollY + 1);
        }, 10);
      }
      this.doAnimation(zone);
      this.DnDdata.processing = false;
    }
  }

  doAnimation(zone, manualReorder) {
    // if entered new zone do animations.
    if (zone !== this.DnDdata.zone) {
      this.DnDdata.zone = zone;

      let hoveredItem = this.hoveredElement(zone);
      // if dragging down

      if (zone > this.DnDdata.grabbedElement.dragOrder) {
        hoveredItem.dragOrder = hoveredItem.dragOrder - 1;
        this.DnDdata.grabbedElement.dragOrder = zone;
        hoveredItem.style = this.getItemStyles(zone - 1);
      }
      // if dragging up
      if (zone < this.DnDdata.grabbedElement.dragOrder) {
        hoveredItem.dragOrder = hoveredItem.dragOrder + 1;
        this.DnDdata.grabbedElement.dragOrder = zone;
        hoveredItem.style = this.getItemStyles(zone + 1);
      }
    }

    if (!manualReorder) {
      // mouse drag
      let parentTop = this.$.items.getBoundingClientRect().y;
      let y =
        this.DnDdata.windowMouseY -
        parentTop -
        this.DnDdata.grabbedElement.clientHeight / 2;

      // stop animation at the top of the container
      y = y > 0 ? y : 0;
      // stop animation at the bottom of the container
      y =
        y + this.DnDdata.grabbedElement.clientHeight > this.$.items.clientHeight
          ? this.$.items.clientHeight - this.DnDdata.grabbedElement.clientHeight
          : y;

      this.DnDdata.grabbedElement.style = this.onTheFlyStyleUpdate(y);
    } else {
      // move button click
      this.DnDdata.grabbedElement.style = this.getItemStyles(zone);
    }
  }

  // On Mouse down handle drag and drop
  _handleMouseDown(e) {
    // remove context menu for mobile
    if (e.changedTouches) {
      window.addEventListener("contextmenu", this._disableContextMenu);
    }

    if (
      e.target.localName === NsSlottedDropdown.is ||
      e.target.parentElement.className === "action" ||
      e.target.localName == "paper-item" ||
      e.target.className === "action"
    )
      return;

    if (!this.interactive && e.preventDefault) e.preventDefault();

    let headerClick = e.target.className.indexOf("actions") !== -1;

    if (this.interactive && !headerClick) return;

    // collect data that is shared accross handlers
    let parentTop = this.$.items.getBoundingClientRect().y;
    this.set("DnDdata", {
      mouseDown: true,
      windowMouseY: e.clientY,
      grabOffset: e.clientY - parentTop,
      grabbedElement: e.currentTarget,
      index: +e.currentTarget.dataset.index,
      processing: false, // used to debounce requestAnimationFrame
      direction: "", // will store direction of the drag ('up' or 'down')
      zone: e.currentTarget.dataset.index, // index of the zone(area) that dragged item is hovering over
      items: Array.prototype.slice.call(
        this.$.items.querySelectorAll(".ns-sortable-item")
      )
    });

    this.DnDdata.grabbedElement.className += " no-transition";
    this.DnDdata.grabbedElement.className += " animated";
    this.DnDdata.grabbedElement.className += " dragged";
    this.DnDdata.grabbedElement.className += " no-touch-action";
    // while animation is running disable pointer events all other items
    this.DnDdata.items.forEach((item) => {
      if (item.dragOrder !== this.DnDdata.grabbedElement.dragOrder)
        item.className += " mute";
    });

    document.addEventListener("touchmove", this._handleTouchMove.bind(this));
    document.addEventListener("touchend", this._handleTouchEnd.bind(this));
    document.addEventListener(
      "mousemove",
      this._handleDocumentMouseMove.bind(this)
    );
    document.addEventListener(
      "mouseup",
      this._handleDocumentMouseUp.bind(this)
    );
  }

  _handleTouchMove(e) {
    if (e.changedTouches) {
      let func = this._handleDocumentMouseMove.bind(this);
      func(e);
    }
  }

  _disableContextMenu(e) {
    if (e.cancelable)
      // e.preventDefault();
      // e.stopPropagation();
      return false;
  }

  _preventDefault(e) {
    if (e.cancelable) e.preventDefault();
  }

  _disableTouchMove() {
    document.addEventListener("touchmove", this._preventDefault);
  }

  _enableTouchMove() {
    document.removeEventListener("touchmove", this._preventDefault);
  }

  _handleTouchStart(e) {
    // needs work still
    // let ev = {
    //   currentTarget: e.currentTarget,
    //   target: e.target,
    //   changedTouches: e.changedTouches,
    //   clientY: e.clientY
    // }
    // this.mobileTouchDebounce = setTimeout(() => {
    //   this._disableTouchMove();
    //   this._handleMouseDown(ev)
    // }, 400);
  }

  _handleTouchEnd(e) {
    clearInterval(this.mobileAutoScroll);
    this._enableTouchMove();
    if (this.mobileTouchDebounce) {
      clearTimeout(this.mobileTouchDebounce);
      let func = this._handleDocumentMouseUp.bind(this);
      func(e);
    }
  }

  _handleActionsMouseOver(e) {
    // if (this.interactive && e.currentTarget.parentElement.className.indexOf('overwrite-background') === -1)
    // e.currentTarget.parentElement.className += ' overwrite-background'
  }

  _handleActionsMouseLeave(e) {
    // if (this.interactive)
    //   e.currentTarget.parentElement.className = e.currentTarget.parentElement.className.split(/\s?overwrite-background/).join('');
  }

  itemIndex(item, index) {
    if (this.propertyForTitle)
      return `${index + 1} - ${item[this.propertyForTitle]}`;
    else return index + 1;
  }

  validate() {
    this.valid = true;
    if (!this.items) this.set("valid", false);
    if (this.required && !this.items.length) this.set("valid", false);
    if (this.minItems < this.items.length) this.set("valid", false);
  }

  removeEventListeners() {
    document.removeEventListener(
      "mousemove",
      this._handleDocumentMouseMove.bind(this)
    );
    document.removeEventListener(
      "mouseup",
      this._handleDocumentMouseUp.bind(this)
    );
    document.removeEventListener(
      "touchmove",
      this._handleTouchMove.bind(this),
      { passive: false }
    );
    document.removeEventListener("touchend", this._handleTouchEnd.bind(this));
    window.removeEventListener("contextmenu", this._disableContextMenu);
  }
}

window.customElements.define(NsSortable.is, NsSortable);
